import { parseCustomFormatToProviderSelections } from '@openx/components-targeting/Targeting/ContextualSegments/utils/enrichment.ts';
import type { TargetingContextualSegmentCreateParams } from '@openx/types/gqlTypes.ts';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing.ts';
import type { CustomVariablesDataPayload, SegmentsOptions } from '@openx/types/targeting/customVariables.ts';
import { toNumber } from '@openx/utils/lib/toNumber.ts';

/**
 * What `packageContextualPricing` returns. Written by hand, not taken from the generated query type, because
 * `pricing_by_org` is a JSON scalar and the prices come as numbers or strings.
 */
export type ContextualPricingResponse =
  | {
      operator?: string | null | undefined;
      pricing_by_org?: unknown;
      totals?: { price_cpm?: unknown; price_pom?: unknown } | null | undefined;
    }
  | null
  | undefined;

/** The contextual segments of the form, as the preview query takes them. Null when no segment is included. */
export const toContextualSegmentInput = (
  segments: CustomVariablesDataPayload[] | null | undefined
): TargetingContextualSegmentCreateParams | null => {
  const selections = parseCustomFormatToProviderSelections(segments ?? []);
  const includes = selections
    .filter(({ includes: ids }) => ids.length > 0)
    .map(({ providerId, includes: val }) => ({ provider: providerId, val }));
  const excludes = selections
    .filter(({ excludes: ids }) => ids.length > 0)
    .map(({ providerId, excludes: val }) => ({ provider: providerId, val }));

  if (includes.length === 0) return null;

  return { includes, ...(excludes.length > 0 ? { excludes } : {}) };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Prices are decimal strings ("0.30"); a finite number is tolerated and normalised to a string. Validated with the
// same parser the fee summary reads them with.
const toPrice = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;

  return Number.isFinite(toNumber(value, { fallback: Number.NaN })) ? String(value) : null;
};

const toPricingByOrg = (value: unknown) => {
  const pricingByOrg: ContextualPricingApi['pricing_by_org'] = {};
  let hasDroppedEntries = false;

  if (!isRecord(value)) return { pricingByOrg, hasDroppedEntries };

  Object.entries(value).forEach(([organizationId, entry]) => {
    const priceCpm = isRecord(entry) ? toPrice(entry['price_cpm']) : null;
    const pricePom = isRecord(entry) ? toPrice(entry['price_pom']) : null;

    // an entry without a valid price is dropped rather than shown as a made-up zero fee
    if (priceCpm === null || pricePom === null) {
      hasDroppedEntries = true;
      return;
    }

    pricingByOrg[organizationId] = {
      ...(isRecord(entry) && typeof entry['account_id'] === 'string' ? { account_id: entry['account_id'] } : {}),
      price_cpm: priceCpm,
      price_pom: pricePom,
    };
  });

  return { pricingByOrg, hasDroppedEntries };
};

/**
 * The API response as `ContextualPricingApi`. Null when nothing is owed. `totals` are passed through only when the
 * API returned both of them and every partner was kept, so they always match the partners shown. They are never
 * worked out here.
 */
export const mapContextualPricing = (response: ContextualPricingResponse): ContextualPricingApi | null => {
  if (!response) return null;

  const { pricingByOrg, hasDroppedEntries } = toPricingByOrg(response.pricing_by_org);

  if (Object.keys(pricingByOrg).length === 0) return null;

  const totalCpm = toPrice(response.totals?.price_cpm);
  const totalPom = toPrice(response.totals?.price_pom);

  return {
    ...(response.operator != null ? { operator: response.operator } : {}),
    pricing_by_org: pricingByOrg,
    ...(!hasDroppedEntries && totalCpm !== null && totalPom !== null
      ? { totals: { price_cpm: totalCpm, price_pom: totalPom } }
      : {}),
  };
};

/**
 * Names the partners of the pricing. The API keys them by `exchange_partner_id` only, while each contextual segment
 * carries that id next to the name of its provider, which is the name the Data Partner filter shows.
 */
export const withPartnerNames = (
  pricing: ContextualPricingApi | null,
  segments: SegmentsOptions
): ContextualPricingApi | null => {
  if (!pricing) return null;

  const namesByPartnerId = new Map<string, string>();

  segments.forEach(({ exchange_partner_id: partnerId, provider_name: providerName }) => {
    if (partnerId && providerName && !namesByPartnerId.has(partnerId)) namesByPartnerId.set(partnerId, providerName);
  });

  return {
    ...pricing,
    pricing_by_org: Object.fromEntries(
      Object.entries(pricing.pricing_by_org).map(([partnerId, entry]) => {
        const name = namesByPartnerId.get(partnerId);

        return [partnerId, name ? { ...entry, name } : entry];
      })
    ),
  };
};
